import React, { useState } from 'react';
import { 
  Phone, 
  Mail, 
  Facebook, 
  Linkedin,
  Building2,
  X,
  Copy
} from 'lucide-react';

// Define translations for LogoModal content
const TRANSLATIONS: Record<string, {
  companyName: string;
  headquarters: string;
  description: string;
  contactTitle: string;
  phone: string;
  email: string;
  socialMedia: string;
}> = {
  en: { // English (United Kingdom)
    companyName: "MADE IN GERMANY AG - UK LIMITED",
    headquarters: "Headquarters: London, United Kingdom",
    description: "Made In Germany engineering in precision manufacturing.",
    contactTitle: "Contact Us",
    phone: "WhatsApp: +49 15753703790",
    email: "Email: info@made-in-germany.uk",
    socialMedia: "Follow us on social media"
  },
  de: { // Deutsch (Germany)
    companyName: "MADE IN GERMANY AG - UK LIMITED",
    headquarters: "Hauptsitz: London, Vereinigtes Königreich",
    description: "Präzision trifft Innovation: Made in Germany – Qualität ohne Kompromisse.",
    contactTitle: "Kontaktieren Sie uns",
    phone: "WhatsApp: +49 15753703790",
    email: "E-Mail: info@made-in-germany.uk",
    socialMedia: "Folgen Sie uns in sozialen Medien"
  },
  es: { // Español (Spain)
    companyName: "MADE IN GERMANY AG - UK LIMITED",
    headquarters: "Sede: Londres, Reino Unido",
    description: "Ingeniería Made In Germany en fabricación de precisión.",
    contactTitle: "Contáctenos",
    phone: "WhatsApp: +49 15753703790",
    email: "Correo: info@made-in-germany.uk",
    socialMedia: "Síguenos en redes sociales"
  },
  fr: { // Français (France)
    companyName: "MADE IN GERMANY AG - UK LIMITED",
    headquarters: "Siège: Londres, Royaume-Uni",
    description: "Ingénierie Made In Germany dans la fabrication de précision.",
    contactTitle: "Contactez-nous",
    phone: "WhatsApp: +49 15753703790",
    email: "Email: info@made-in-germany.uk",
    socialMedia: "Suivez-nous sur les réseaux sociaux"
  },
  it: { // Italiano (Italy)
    companyName: "MADE IN GERMANY AG - UK LIMITED",
    headquarters: "Sede: Londra, Regno Unito",
    description: "Ingegneria Made In Germany nella produzione di precisione.",
    contactTitle: "Contattaci",
    phone: "WhatsApp: +49 15753703790",
    email: "Email: info@made-in-germany.uk",
    socialMedia: "Seguici sui social media"
  },
  nl: { // Dutch (Netherlands)
    companyName: "MADE IN GERMANY AG - UK LIMITED",
    headquarters: "Hoofdkantoor: Londen, Verenigd Koninkrijk",
    description: "Made In Germany ingenieurskunst in precisiefabricage.",
    contactTitle: "Neem contact met ons op",
    phone: "WhatsApp: +49 15753703790",
    email: "E-mail: info@made-in-germany.uk",
    socialMedia: "Volg ons op sociale media"
  },
  sa: { // العربية (السعودية) - Arabic (Saudi Arabia)
    companyName: "MADE IN GERMANY AG - UK LIMITED",
    headquarters: "المقر الرئيسي: لندن، المملكة المتحدة",
    description: "هندسة Made In Germany في التصنيع الدقيق.",
    contactTitle: "اتصل بنا",
    phone: "واتساب: +49 15753703790",
    email: "البريد الإلكتروني: info@made-in-germany.uk",
    socialMedia: "تابعنا على وسائل التواصل الاجتماعي"
  },
  hk: { // 繁體中文 (Hong Kong) - Traditional Chinese
    companyName: "MADE IN GERMANY AG - UK LIMITED",
    headquarters: "總部：倫敦，英國",
    description: "Made In Germany 工程在精密製造中。",
    contactTitle: "聯繫我們",
    phone: "WhatsApp：+49 15753703790",
    email: "電子郵件：info@made-in-germany.uk",
    socialMedia: "在社交媒體上關注我們"
  },
  sg: { // 简体中文 (Singapore) - Simplified Chinese
    companyName: "MADE IN GERMANY AG - UK LIMITED",
    headquarters: "总部：伦敦，英国",
    description: "Made In Germany 工程在精密制造中。",
    contactTitle: "联系我们",
    phone: "WhatsApp：+49 15753703790",
    email: "电子邮件：info@made-in-germany.uk",
    socialMedia: "在社交媒体上关注我们"
  },
  za: { // Afrikaans (South Africa)
    companyName: "MADE IN GERMANY AG - UK LIMITED",
    headquarters: "Hoofkantoor: Londen, Verenigde Koninkryk",
    description: "Made In Germany ingenieurskunst in presisievervaardiging.",
    contactTitle: "Kontak ons",
    phone: "WhatsApp: +49 15753703790",
    email: "E-pos: info@made-in-germany.uk",
    socialMedia: "Volg ons op sosiale media"
  },
  kr: { // 한국어 (South Korea) - Korean
    companyName: "MADE IN GERMANY AG - UK LIMITED",
    headquarters: "본사: 런던, 영국",
    description: "Made In Germany 엔지니어링 정밀 제조.",
    contactTitle: "문의하기",
    phone: "왓츠앱: +49 15753703790",
    email: "이메일: info@made-in-germany.uk",
    socialMedia: "소셜 미디어에서 팔로우하세요"
  },
  jp: { // 日本語 (Japan) - Japanese
    companyName: "MADE IN GERMANY AG - UK LIMITED",
    headquarters: "本社：ロンドン、イギリス",
    description: "Made In Germany 工学による精密製造。",
    contactTitle: "お問い合わせ",
    phone: "ワッツアップ: +49 15753703790",
    email: "メール：info@made-in-germany.uk",
    socialMedia: "ソーシャルメディアでフォローしてください"
  }
};

interface LogoModalProps {
  isVisible?: boolean;
  onClose: () => void;
  language?: string;
  isScrolled?: boolean; // Added to handle scroll state
}

const LogoModal = ({ isVisible = false, onClose, language = 'en', isScrolled = false }: LogoModalProps) => {
  const [copyStatus, setCopyStatus] = useState<string>('');

  if (!isVisible) {
    return null;
  }

  const t = TRANSLATIONS[language] || TRANSLATIONS['en'];
  const phoneNumber = t.phone.replace(/[^\d+]/g, '');
  const emailAddress = t.email.split(': ')[1] || t.email;

  const handleCopyPhone = () => {
    navigator.clipboard.writeText(phoneNumber)
      .then(() => {
        setCopyStatus('Phone number copied');
        setTimeout(() => setCopyStatus(''), 2000);
      })
      .catch((err) => console.error('Failed to copy phone number:', err));
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(emailAddress)
      .then(() => {
        setCopyStatus('Email copied');
        setTimeout(() => setCopyStatus(''), 2000);
      })
      .catch((err) => console.error('Failed to copy email:', err));
  };

  // Dynamically adjust top based on scroll state
  const modalTop = isScrolled ? '80px' : '120px'; // 64px matches header height when scrolled

  return (
    <div 
      className="fixed z-50 animate-slide-in w-full max-w-[550px] left-0 md:left-5" 
      style={{ top: modalTop }} // Use dynamic top value
    >
      <div className="mx-4 md:mx-0">
        <div className="shadow-2xl rounded-xl overflow-hidden relative">
          <button 
            onClick={onClose}
            className="absolute right-2 top-2 z-10 w-6 h-6 rounded-full bg-gray-200/50 flex items-center justify-center hover:bg-gray-300/50 transition-colors"
          >
            <X className="h-4 w-4 text-gray-700" />
          </button>

          <div className="flex flex-col md:flex-row">
            <div className="bg-[#0B111F] text-white p-4 w-full md:w-1/2">
              <div className="h-full flex flex-col justify-between">
                <div className="flex mb-3">
                  <img
                    src={localStorage.getItem("customIcon") || "/made-in-germany-logo-white.webp"} 
                    alt="Made in Germany"
                    className="w-50 h-50 object-contain"
                  />
                </div>

                <div className="mb-4 space-y-3">
                  <h2 className="text-xs font-bold whitespace-nowrap">{t.companyName}</h2>
                  <ul className="space-y-2 text-gray-300 text-xs">
                    <li className="flex items-center gap-2">
                      <Building2 className="h-3 w-3 text-[#FFFFFF]" />
                      {t.headquarters}
                    </li>
                  </ul>
                </div>

                <div className="text-xs font-medium text-gray-300 border-l-2 border-[#FFFFFF] pl-2">
                  {t.description}
                </div>
              </div>
            </div>

            <div className="bg-[#0B111F] text-white p-4 w-full md:w-1/2 relative flex items-center">
              <div className="space-y-4 w-full">
                <div className="space-y-2">
                  <h3 className="text-sm font-bold">{t.contactTitle}</h3>
                  <div className="space-y-1">
                    <div className="flex items-center text-xs gap-2 group">
                      <a 
                        href={`https://wa.me/${phoneNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 hover:text-gray-300 transition-colors"
                      >
                        <Phone className="h-3 w-3 group-hover:rotate-12 transition-transform" />
                        {t.phone}
                      </a>
                      <button
                        onClick={handleCopyPhone}
                        className="p-1 hover:bg-white/10 rounded transition-colors"
                        title="Copy phone number"
                      >
                        <Copy className={`h-3 w-3 ${copyStatus === 'Phone number copied' ? 'text-green-400' : 'text-gray-300'}`} />
                      </button>
                    </div>
                    <div className="flex items-center text-xs gap-2 group">
                      <a 
                        href={`mailto:${emailAddress}`}
                        className="flex items-center gap-2 hover:text-gray-300 transition-colors"
                      >
                        <Mail className="h-3 w-3 group-hover:rotate-12 transition-transform" />
                        {t.email}
                      </a>
                      <button
                        onClick={handleCopyEmail}
                        className="p-1 hover:bg-white/10 rounded transition-colors"
                        title="Copy email"
                      >
                        <Copy className={`h-3 w-3 ${copyStatus === 'Email copied' ? 'text-green-400' : 'text-gray-300'}`} />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs">{t.socialMedia}</p>
                  <div className="flex gap-2">
                    <a 
                      href="mailto:info@made-in-germany.uk"
                      className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors group"
                      title="Email"
                    >
                      <Mail className="h-3 w-3 group-hover:scale-110 transition-transform" />
                    </a>
                    <a 
                      href="https://www.facebook.com/madeingermany" // Replace with actual link
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors group"
                      title="Facebook"
                    >
                      <Facebook className="h-3 w-3 group-hover:scale-110 transition-transform" />
                    </a>
                    <a 
                      href="https://www.linkedin.com/company/madeingermany" // Replace with actual link
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors group"
                      title="LinkedIn"
                    >
                      <Linkedin className="h-3 w-3 group-hover:scale-110 transition-transform" />
                    </a>
                  </div>
                </div>
              </div>
              {/* Copy Status Message */}
              {copyStatus && (
                <div className="absolute bottom-2 left-4 right-4 bg-green-600/80 text-white text-xs py-1 px-2 rounded text-center transition-opacity duration-300">
                  {copyStatus}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoModal;